import { useState, useRef, useEffect, MutableRefObject } from 'react';
import {
  motion,
  useMotionValue,
  useTransform,
  useAnimationFrame,
  MotionValue,
} from 'framer-motion';
import styles from './radialLayout.module.css';
import { PlayerPostion, RadialLayoutProps } from './radialLayout.types';
import { PlayerWithRisk } from '../dashboard.type';
import PlayerIcon from '../../../component/playerIcon/playerIcon';

const RadialLayout = (props: RadialLayoutProps) => {
  const outerCount: number = 18;
  const innerOuterCount: number = 11;
  const innerMiddleCount: number = 6;

  const rotate: MotionValue<number> = useMotionValue(0);
  const [isOuterHovered, setIsOuterHovered] = useState<boolean>(false);

  const lastTimestamp: MutableRefObject<number> = useRef(0);
  const [playersPosition, setPlayersPosition] = useState<PlayerPostion>({
    center: undefined,
    outerRing: [], // Will contain 18 elements
    innerOuterRing: [], // Will contain 11 elements
    innerMiddleRing: [], // Will contain 6 elements
  });
  const [radialDimensions, setRadialDimensions] = useState<{
    height: string | undefined;
    width: string | undefined;
  }>({ height: '100%', width: undefined });

  useEffect(() => {
    if (!props.playerLoading) {
      let innerPlayers: Array<PlayerWithRisk | undefined> = [
        ...props.playersData.excluded,
        ...props.playersData.intervention_required,
      ].slice(0, 18);
      let outerPlayers: Array<PlayerWithRisk | undefined> = [
        ...props.playersData.low,
        ...props.playersData.intervention_offered,
      ].slice(0, 18);

      let tempPlayers: PlayerPostion = {
        center: undefined,
        outerRing: [],
        innerOuterRing: [],
        innerMiddleRing: [],
      };

      if (innerPlayers.length > 0) {
        tempPlayers.center = innerPlayers[0];

        const middleRing: (PlayerWithRisk | undefined)[] = innerPlayers.slice(
          1,
          7,
        );
        while (middleRing.length < 6) middleRing.push(undefined);
        tempPlayers.innerMiddleRing = middleRing;

        const innerOuterRing: (PlayerWithRisk | undefined)[] =
          innerPlayers.slice(7, 18);
        while (innerOuterRing.length < 11) innerOuterRing.push(undefined);
        tempPlayers.innerOuterRing = innerOuterRing;
      } else {
        tempPlayers.innerMiddleRing = new Array(6).fill(undefined);
        tempPlayers.innerOuterRing = new Array(11).fill(undefined);
      }

      const outerRing: (PlayerWithRisk | undefined)[] = outerPlayers.slice(
        0,
        18,
      );
      while (outerRing.length < 18) outerRing.push(undefined);
      tempPlayers.outerRing = outerRing;
      setPlayersPosition(tempPlayers);
    }
  }, [props.playersData, props.playerLoading]);

  useEffect(() => {
    const height: number = props.dimensions.height;
    const width: number = props.dimensions.width;
    const relativeHeightPercentage: number = (height / width) * 100;

    if (width > height) {
      if (relativeHeightPercentage < 80) {
        setRadialDimensions({
          height: '100%',
          width: undefined,
        });
      } else if (relativeHeightPercentage < 87) {
        setRadialDimensions({
          height: '90%',
          width: undefined,
        });
      } else {
        setRadialDimensions({
          height: '80%',
          width: undefined,
        });
      }
    } else {
      setRadialDimensions({
        height: undefined,
        width: '75%',
      });
    }
  }, [props.dimensions]);

  useAnimationFrame((t) => {
    if (!isOuterHovered) {
      if (lastTimestamp.current === 0) {
        lastTimestamp.current = t;
        return;
      }
      const delta = t - lastTimestamp.current;
      lastTimestamp.current = t;

      const rotationSpeed = 1.5;
      const rotationIncrement = (delta / 1000) * rotationSpeed;

      rotate.set(rotate.get() + rotationIncrement);
    } else {
      lastTimestamp.current = 0;
    }
  });

  const counterRotate: MotionValue<number> = useTransform(
    rotate,
    (latest) => -latest,
  );

  const innerOuterRadius: number = 40;
  const innerMiddleRadius: number = 20;

  const renderInnerRing = (
    count: number,
    radius: number,
    startIndex: number,
    data: Array<PlayerWithRisk | undefined>,
  ) => {
    return data.map((player, i) => {
      const angle = (2 * Math.PI * i) / count - Math.PI / 2;
      const x = 50 + radius * Math.cos(angle);
      const y = 50 + radius * Math.sin(angle);

      return (
        <div
          key={startIndex + i}
          className={`${styles.placeholder} ${styles.hidden}`}
          style={{
            left: `${x}%`,
            top: `${y}%`,
            transform: 'translate(-50%, -50%)',
            position: 'absolute',
          }}
        >
          {player && (
            <PlayerIcon
              player={player}
              key={startIndex + i}
              onClick={() => props.onPlayerClick(player)}
            />
          )}
        </div>
      );
    });
  };

  const renderSpokedLines = () => {
    const total: number = 400;
    const diameter: string = '70%';

    const lines: JSX.Element[] = Array.from({ length: total }).map((_, i) => {
      const angle: number = (360 / total) * i;

      return (
        <div
          key={i}
          className={styles.spokedLine}
          style={{
            transform: `rotate(${angle}deg)`,
          }}
        />
      );
    });

    return (
      <div
        style={{
          height: diameter,
        }}
        className={styles.spokedLines}
      >
        {lines}
      </div>
    );
  };

  return (
    <>
      <div
        className={styles.ring}
        style={{
          height: radialDimensions.height,
          width: radialDimensions.width,
        }}
      >
        <div className={styles.ringOverlay}></div>
        {renderSpokedLines()}
        <motion.div
          style={{ rotate }}
          className={styles.outerRing}
          onHoverStart={() => setIsOuterHovered(true)}
          onHoverEnd={() => setIsOuterHovered(false)}
        >
          {playersPosition.outerRing.map((player, index) => {
            const angle = (2 * Math.PI * index) / outerCount - Math.PI / 2;
            const x = 50 + 42.5 * Math.cos(angle);
            const y = 50 + 42.5 * Math.sin(angle);

            return (
              <div
                key={index}
                className={`${styles.placeholder} ${styles.hidden}`}
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: 'translate(-50%, -50%)',
                  position: 'absolute',
                }}
              >
                <motion.div
                  className={styles.motionWrapper}
                  style={{ rotate: counterRotate }}
                  whileHover={{ scale: 1.2 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  {player && (
                    <PlayerIcon
                      player={player}
                      onClick={() => props.onPlayerClick(player)}
                    />
                  )}
                </motion.div>
              </div>
            );
          })}
        </motion.div>

        <div className={styles.innerCircleGap}>
          <div className={styles.innerCircle} style={{ position: 'relative' }}>
            {renderInnerRing(
              innerOuterCount,
              innerOuterRadius,
              0,
              playersPosition.innerOuterRing,
            )}
            {renderInnerRing(
              innerMiddleCount,
              innerMiddleRadius,
              innerOuterCount,
              playersPosition.innerMiddleRing,
            )}

            <div
              className={`${styles.placeholder} ${styles.hidden}`}
              style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                position: 'absolute',
              }}
            >
              {playersPosition.center && (
                <PlayerIcon
                  player={playersPosition.center}
                  onClick={() =>
                    props.onPlayerClick(
                      playersPosition.center as PlayerWithRisk,
                    )
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RadialLayout;
